import jwtDecode from 'jwt-decode';
import { BaseChatService } from 'shared/services/baseChatService';
import { type DataPayload } from 'types/customEventData';
import { handleAsync, handleError } from 'utils/errorHandler';
import { type SmoochChatClient } from 'v2/clients/smoochChatClient';
import { type SupportClient } from 'v2/clients/supportClient';
import { DEFAULT_SMOOCH_INIT_OPTIONS } from 'v2/constants/smoochInitOptions';
import { localeConfigs } from 'v2/locales';
import { getBrowserLocale } from 'v2/utils/localeHelpers';

export class SmoochChatService extends BaseChatService<SmoochChatClient> {
  private readonly supportClient: SupportClient;

  private readonly scope: string;

  /**
   * Constructor for initializing the messagingClient and authService.
   */
  public constructor(
    smoochClient: SmoochChatClient,
    supportClient: SupportClient,
    scope: string,
  ) {
    super(smoochClient);
    this.supportClient = supportClient;
    this.scope = scope;
  }

  /**
   * Initiates the chat.
   */
  public initiateChat = async ({
    tags,
    conversation_fields: conversationFields,
    messaging_token: messagingToken,
  }: DataPayload) => {
    try {
      let token = messagingToken;

      if (!token) {
        token = await this.supportClient.getUserToken(this.scope);
      }

      const { external_id: externalId } = jwtDecode<{ external_id: string }>(
        token,
      );

      const initConfig = this.getInitConfig();

      await this.zendeskClient.init({
        ...initConfig,
        jwt: token,
        externalId,
        delegate: {
          beforeDisplay: this.beforeDisplayHandler,
          onInvalidAuth: () => this.supportClient.getUserToken(this.scope),
        },
      });

      this.zendeskClient.onReceivedMessage(this.onReceivedMessageHandler);

      await super.initiateChat({
        tags,
        conversation_fields: conversationFields,
      });
    } catch (error) {
      handleError(error);
    }
  };

  private getInitConfig = (): Partial<InitOptions> => {
    const locale = getBrowserLocale(window.navigator);
    const localeConfig = localeConfigs[locale] || localeConfigs.es;

    return {
      ...DEFAULT_SMOOCH_INIT_OPTIONS,
      ...localeConfig,
    };
  };

  private beforeDisplayHandler = (message: Message) => {
    if (message.metadata?.ticket_status === 'closed') {
      return null;
    }

    return message;
  };

  private onReceivedMessageHandler = (message: Message) => {
    if (message.metadata?.ticket_status === 'closed') {
      handleAsync(() => this.zendeskClient.hideWidget());
      return null;
    }

    return message;
  };
}
